import React, { type ReactNode } from 'react';

import { usePanelStateInternal } from '../panel-manager/use-panel-state-internal';
import { PanelContext } from './panel-context';

/**
 * Props for the PanelsRenderer component
 */
export type PanelsRendererProps = {
	/**
	 * Component to render for the active panel.
	 * The panel instance is automatically wrapped in PanelContext.Provider,
	 * so you can use useCurrentPanel() within your component to access panel data.
	 *
	 * @example
	 * ```tsx
	 * <PanelsRenderer>
	 *   <CustomPanel />
	 * </PanelsRenderer>
	 *
	 * function CustomPanel() {
	 *   const panel = useCurrentPanel();
	 *
	 *   return (
	 *     <PanelContainer>
	 *       <PanelHeader>
	 *         <PanelTitle>{panel.params?.title}</PanelTitle>
	 *         <PanelActionClose />
	 *       </PanelHeader>
	 *       <PanelBody>{panel.params?.content}</PanelBody>
	 *     </PanelContainer>
	 *   );
	 * }
	 * ```
	 */
	children?: ReactNode;
};

/**
 * PanelsRenderer component that renders the active panel.
 *
 * This is the companion to the Trigger component. While Trigger opens panels,
 * PanelsRenderer displays the currently open panel.
 *
 * The panel instance is automatically wrapped in PanelContext.Provider, enabling
 * useCurrentPanel() for clean access to panel data without prop drilling.
 *
 * Simply provide a component as children. PanelsRenderer will automatically render
 * it for the active panel, wrapped in its own context.
 *
 * Use panel-system UI components (PanelContainer, PanelHeader, PanelBody, etc.)
 * to compose your panel UI within your component.
 *
 * Must be used within a PanelProvider.
 *
 * @example
 * ```tsx
 * <PanelsRenderer>
 *   <CustomPanel />
 * </PanelsRenderer>
 *
 * function CustomPanel() {
 *   const panel = useCurrentPanel();
 *
 *   return (
 *     <PanelContainer>
 *       <PanelHeader>
 *         <PanelTitle>{panel.params?.title}</PanelTitle>
 *         <PanelActionClose />
 *       </PanelHeader>
 *       <PanelBody>{panel.params?.content}</PanelBody>
 *     </PanelContainer>
 *   );
 * }
 * ```
 */
export function PanelsRenderer({ children }: PanelsRendererProps): React.JSX.Element | null {
	const { activePanel } = usePanelStateInternal();

	if (!activePanel) {
		return null;
	}

	return (
		<PanelContext.Provider key={activePanel.instanceId} value={activePanel}>
			{children}
		</PanelContext.Provider>
	);
}
